import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';
import {
  AlbumItemSchema,
  PlaylistItemSchema,
  StationItemSchema,
} from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

const DEFAULT_MAX_RECENTLY_PLAYED =
  import.meta.env.VITE_DEFAULT_MAX_RECENT_PLAYED
    ? Number(import.meta.env.VITE_DEFAULT_MAX_RECENT_PLAYED)
    : 10;

export const MeRecentlyPlayedRequestArgsSchema = pipe(
  object({
    limit: optional(number()),
    offset: optional(number()),
  }),
  transform(({ limit, offset }) => {
    return {
      limit: limit ?? DEFAULT_MAX_RECENTLY_PLAYED,
      offset: offset ?? 0,
      types: [
        'albums',
        'playlists',
        'stations',
        'library-albums',
        'library-playlists',
      ],
    };
  }),
);

export const RecentlyPlayedResponseSchema = pipe(
  object({
    data: array(
      union([AlbumItemSchema, PlaylistItemSchema, StationItemSchema]),
    ),
    next: optional(string()),
  }),
  transform(({ data, next }) => {
    return {
      recentlyPlayed: data,
      next: next === undefined ? undefined : getOffsetNumber(next),
    };
  }),
);

export const MeRecentlyPlayedAPIResponseSchema = object({
  data: RecentlyPlayedResponseSchema,
});

export type MeRecentlyPlayedRequestArgs = InferOutput<
  typeof MeRecentlyPlayedRequestArgsSchema
>;

export type MeRecentlyPlayedRequestArgsInput = InferInput<
  typeof MeRecentlyPlayedRequestArgsSchema
>;

export type RecentlyPlayedResponse = InferOutput<
  typeof RecentlyPlayedResponseSchema
>;

export type MeRecentlyPlayedAPIResponseInput = InferInput<
  typeof MeRecentlyPlayedAPIResponseSchema
>;
export type MeRecentlyPlayedAPIResponse = InferOutput<
  typeof MeRecentlyPlayedAPIResponseSchema
>;

export const $getMeRecentlyPlayed = async (
  player: MusicKit.MusicKitInstance,
  params: MeRecentlyPlayedRequestArgsInput,
): Promise<RecentlyPlayedResponse> => {
  const arg = safeParse(
    MeRecentlyPlayedRequestArgsSchema,
    params,
  );
  if (!arg.success) {
    throw new ValiError(arg.issues);
  }
  const response = await player.api.music(
    '/v1/me/recent/played',
    arg.output,
  ) as unknown as MeRecentlyPlayedAPIResponseInput;
  const parsedResponse = safeParse(
    MeRecentlyPlayedAPIResponseSchema,
    response,
  );
  if (!parsedResponse.success) {
    throw new ValiError(parsedResponse.issues);
  }

  return parsedResponse.output.data;
};
